import { useEffect, useState } from 'react';
import { pinGuardCheck, pinGuardRecord, verifyPin, type PinGuardState } from '@makila/pos-core';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { Operator } from '../screens/Lock';

/**
 * Autorização de um gerente (devoluções, descontos): escolher o gerente e o
 * PIN dele, verificado no terminal, com o mesmo limite de tentativas da
 * entrada. Se quem está na caixa já é gerente, autoriza logo.
 */
export function ManagerApproval({ store, operator, why, onApproved }: { store: IdbTerminalStore; operator: Operator; why: string; onApproved: (m: Operator) => void }) {
  const [managers, setManagers] = useState<Operator[]>([]);
  const [mgr, setMgr] = useState<Operator | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (operator.role === 'manager') { onApproved(operator); return; }
    void store.catalog('operator').then((i) => setManagers(i.map((x) => x.data as unknown as Operator).filter((o) => o.active && o.role === 'manager')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, operator]);

  const approve = async () => {
    if (!mgr) return;
    const key = `pinGuard:${mgr.id}`;
    const g = await store.kv<PinGuardState>(key);
    if (!pinGuardCheck(g, Date.now()).allowed) { setError('Gerente bloqueado por tentativas a mais. Espere uns minutos.'); return; }
    const ok = await verifyPin(pin, mgr.pin);
    await store.setKv(key, pinGuardRecord(g, ok, Date.now()));
    setPin('');
    if (ok) onApproved(mgr); else setError('PIN do gerente errado.');
  };

  if (operator.role === 'manager') return null;
  return (
    <div>
      <p className="text-muted">{why}</p>
      {!managers.length && <p className="mt-3 rounded-lg bg-amber-100 p-3 text-amber-900">Não há gerentes nesta caixa. Crie um operador com perfil Gerente na gestão.</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {managers.map((m) => <button key={m.id} onClick={() => { setMgr(m); setError(null); }} className={`h-12 rounded-xl border px-4 font-medium ${mgr?.id === m.id ? 'border-brand-700 bg-brand-700 text-white' : 'border-base'}`}>{m.name}</button>)}
      </div>
      {mgr && (
        <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); void approve(); }}>
          <input autoFocus type="password" inputMode="numeric" autoComplete="off" aria-label="PIN do gerente" placeholder="PIN do gerente" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className="h-12 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-center text-xl tracking-widest" />
          <button className="h-12 rounded-xl bg-brand-700 px-5 font-bold text-white">Autorizar</button>
        </form>
      )}
      {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
    </div>
  );
}
